import { PublicKey } from "@solana/web3.js";
import axios from "axios";
// @ts-ignore
import base58 from "bs58";
export async function getAsset(url, assetId) {
    try {
        const response = await axios.post(url, {
            jsonrpc: "2.0",
            method: "getAsset",
            id: "rpd-op-123",
            params: { id: assetId.toBase58() },
            headers: {
                "Cache-Control": "no-cache",
                Pragma: "no-cache",
                Expires: "0",
            },
        });
        const result = response.data.result;
        if (result) {
            return toAsset(result);
        }
    }
    catch (error) {
        console.error(error);
        throw error;
    }
}
export async function getAssetBatch(url, assetIds) {
    try {
        const response = await axios.post(url, {
            jsonrpc: "2.0",
            method: "getAssetBatch",
            id: "rpd-op-123",
            params: { ids: assetIds.map((assetId) => assetId.toBase58()) },
            headers: {
                "Cache-Control": "no-cache",
                Pragma: "no-cache",
                Expires: "0",
            },
        });
        const result = response.data.result;
        if (result) {
            return result.map(toAsset);
        }
    }
    catch (error) {
        console.error(error);
        throw error;
    }
}
export async function getAssets(url, assetIds) {
    try {
        if (assetIds.length > 1000) {
            throw new Error(`Can only batch 1000 at a time, was given ${assetIds.length}`);
        }
        const batch = assetIds.map((assetId, i) => ({
            jsonrpc: "2.0",
            id: `get-asset-${i}`,
            method: "getAsset",
            params: {
                id: assetId.toBase58(),
            },
        }));
        const response = await axios({
            url,
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Cache-Control": "no-cache",
                Pragma: "no-cache",
                Expires: "0",
            },
            data: JSON.stringify(batch),
        });
        const result = response.data
            ? response.data.map((res) => res?.result || undefined)
            : [];
        return [
            ...(result
                ? result.map((x) => (x ? toAsset(x) : x))
                : []),
        ];
    }
    catch (error) {
        console.error(error);
        throw error;
    }
}
function toAsset(result) {
    return {
        ...result,
        creators: result.creators.map(({ address, share, verified, }) => ({
            share,
            verified,
            address: address && new PublicKey(address),
        })),
        id: new PublicKey(result.id),
        grouping: result.grouping &&
            result.grouping.map((g) => ({
                ...g,
                group_value: new PublicKey(g.group_value),
            })),
        compression: {
            ...result.compression,
            leafId: result.compression.leaf_id,
            dataHash: result.compression.data_hash &&
                Buffer.from(base58.decode(result.compression.data_hash)),
            creatorHash: result.compression.creator_hash &&
                Buffer.from(base58.decode(result.compression.creator_hash)),
            assetHash: result.compression.asset_hash &&
                Buffer.from(base58.decode(result.compression.asset_hash)),
            tree: result.compression.tree && new PublicKey(result.compression.tree),
        },
        ownership: {
            ...result.ownership,
            delegate: result.ownership.delegate && new PublicKey(result.ownership.delegate),
            owner: result.ownership.owner && new PublicKey(result.ownership.owner),
        },
    };
}
export async function getAssetProof(url, assetId) {
    try {
        const response = await axios.post(url, {
            jsonrpc: "2.0",
            method: "getAssetProof",
            id: "rpd-op-123",
            params: { id: assetId.toBase58() },
            headers: {
                "Cache-Control": "no-cache",
                Pragma: "no-cache",
                Expires: "0",
            },
        });
        const result = response.data.result;
        if (result) {
            return {
                root: new PublicKey(result.root),
                proof: result.proof.map((p) => new PublicKey(p)),
                nodeIndex: result.node_index,
                leaf: new PublicKey(result.leaf),
                treeId: new PublicKey(result.tree_id),
            };
        }
    }
    catch (error) {
        console.error(error);
        throw error;
    }
}
export async function getAssetProofBatch(url, assetIds) {
    try {
        const response = await axios.post(url, {
            jsonrpc: "2.0",
            method: "getAssetProofBatch",
            id: "rpd-op-123",
            params: { ids: assetIds.map((assetId) => assetId.toBase58()) },
            headers: {
                "Cache-Control": "no-cache",
                Pragma: "no-cache",
                Expires: "0",
            },
        });
        const result = response.data.result;
        if (result) {
            return Object.entries(result).reduce((acc, [k, r]) => {
                acc[k] = {
                    root: new PublicKey(r.root),
                    proof: r.proof.map((p) => new PublicKey(p)),
                    nodeIndex: r.node_index,
                    leaf: new PublicKey(r.leaf),
                    treeId: new PublicKey(r.tree_id),
                };
                return acc;
            }, {});
        }
    }
    catch (error) {
        console.error(error);
        throw error;
    }
}
export async function getAssetsByOwner(url, wallet, { sortBy = { sortBy: "created", sortDirection: "asc" }, limit = 50, page = 1, before = "", after = "", } = {}) {
    try {
        const response = await axios.post(url, {
            jsonrpc: "2.0",
            method: "getAssetsByOwner",
            id: "rpd-op-123",
            params: [wallet, sortBy, limit, page, before, after],
            headers: {
                "Cache-Control": "no-cache",
                Pragma: "no-cache",
                Expires: "0",
            },
        });
        return response.data.result?.items.map(toAsset);
    }
    catch (error) {
        console.error(error);
        throw error;
    }
}
export async function searchAssets(url, opts) {
    return (await searchAssetsWithPageInfo(url, opts)).items;
}
export async function searchAssetsWithPageInfo(url, { creatorVerified = true, sortBy = { sortBy: "created", sortDirection: "asc" }, page = 1, limit = 1000, collection, tokenType, ...rest }) {
    const params = {
        page,
        limit,
        sortBy: tokenType && ["all", "fungible"].includes(tokenType) ? null : sortBy,
        creatorVerified: tokenType && ["all", "fungible"].includes(tokenType)
            ? null
            : creatorVerified,
        tokenType,
        ...(collection ? { grouping: ["collection", collection] } : {}),
        ...rest,
    };
    try {
        const response = await axios.post(url, {
            jsonrpc: "2.0",
            method: "searchAssets",
            id: "get-assets-op-1",
            params,
            headers: {
                "Cache-Control": "no-cache",
                Pragma: "no-cache",
                Expires: "0",
            },
        });
        const ret = response.data.result;
        return {
            items: ret.items?.map(toAsset),
            limit: ret.limit,
            total: ret.total,
            page: ret.page,
            grandTotal: ret.grand_total,
        };
    }
    catch (error) {
        console.error(error);
        throw error;
    }
}
//# sourceMappingURL=mplAssetAPI.js.map